import React, { useMemo, useState } from 'react';
import { Row, Col, Empty, Pagination, message } from 'antd';
import startCase from 'lodash/startCase';
import DashboardRow from '../../../shared/DashboardRow';
import Item from './Item';

import { DASHBOARD_TYPES, GQL_InvestigationTeacherEntry } from '../../../types/investigation';
import { formatDistance } from '../../../utils/date';
import InvestigationIcon from '../../../shared/InvestigationIcon';
import DashboardLoadingCard from '../../../shared/DashboardLoadingCard';
import { useApolloClient, useQuery } from '@apollo/client';
import { gqlSchema } from '../../../gql/schema';

interface IActiveAssessmentsRow {
  assessments?: GQL_InvestigationTeacherEntry[];
  loading?: boolean;
}

const ActiveAssessmentsRow = (props: IActiveAssessmentsRow) => {
  const pageSize = 3;
 const [pageActiveAssess, setPageActiveAssess] = useState(1);
 const client = useApolloClient();

  // const startIndex = (pageActiveAssess - 1) * pageSize;
  // const endIndex = startIndex + pageSize;
  const { data, loading } = useQuery<{
    getInvestigationsByTeacherLimited: GQL_InvestigationTeacherEntry[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByTeacherLimited, {
    variables: { filter: { page: pageActiveAssess, pageSize, type: DASHBOARD_TYPES.ACTIVE_ASSESSMENTS } },
    onError: (error) => {
      message.error(error.message || 'Error loading investigations.');
    },
    fetchPolicy: 'cache-and-network',
  });

  const assessments = data?.getInvestigationsByTeacherLimited || [];

  const paginatedAssesments = assessments;

  const activeAssessments = useMemo(() => {
    if (loading) {
      return (
        <Row gutter={[24, 24]}>
          <Col xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}>
            <DashboardLoadingCard />
          </Col>
          <Col xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}>
            <DashboardLoadingCard />
          </Col>
          <Col xxl={8} xl={8} lg={12} md={24} sm={24} xs={24}>
            <DashboardLoadingCard />
          </Col>
        </Row>
      );
    } else if (!assessments?.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Active Assessments" />;
    } else {
      return (
        <Row gutter={[24, 24]}>
          {paginatedAssesments?.map((item: GQL_InvestigationTeacherEntry, index) => {
            return (
              <Col xxl={8} xl={8} lg={12} md={24} sm={24} xs={24} key={`${item.id}-${index}`}>
                <Item
                  className={item.class}
                  progress={item.completion * 100}
                  classColor={item.classColorHex}
                  timeLeft={`${formatDistance(item.dueDate)} left`}
                  assessmentName={`${startCase(item.discipline?.name?.toLowerCase())}: ${item.title}`}
                  assessmentId={item.id}
                  timeWarning={false}
                  type={'Assessment'}
                  classId={item.classId}
                  version={item.submissionVersion}
                  icon={
                    <InvestigationIcon
                      subject={item.discipline?.subject || ''}
                      discipline={item.discipline?.name || ''}
                    />
                  }
                />
              </Col>
            );
          })}
        </Row>
      );
    }
  }, [assessments, loading, paginatedAssesments]);

  // const [extraActiveAssessments, setActiveAssessments] = useState<ReactElement | null>(null);

  // const onViewMoreActiveAssessments = useCallback(
  //   (opened: boolean) => {
  //     if (opened)
  //       setActiveAssessments(
  //         <Row gutter={[24, 24]}>
  //           {assessments?.slice(3, assessmentsLength)?.map((item: GQL_InvestigationTeacherEntry, index) => {
  //             return (
  //               <Col xxl={8} xl={8} lg={12} md={24} sm={24} xs={24} key={`${item.id}-${index}`}>
  //                 <Item
  //                   className={item.class}
  //                   progress={item.completion * 100}
  //                   classColor={item.classColorHex}
  //                   timeLeft={`${formatDistance(item.dueDate)} left`}
  //                   assessmentName={`${startCase(item.discipline?.name?.toLowerCase())}: ${item.title}`}
  //                   assessmentId={item.id}
  //                   timeWarning={false}
  //                   type={'Assessment'}
  //                   classId={item.classId}
  //                   version={item.submissionVersion}
  //                   icon={
  //                     <InvestigationIcon
  //                       subject={item.discipline?.subject || ''}
  //                       discipline={item.discipline?.name || ''}
  //                     />
  //                   }
  //                 />
  //               </Col>
  //             );
  //           })}
  //         </Row>,
  //       );
  //     else setActiveAssessments(null);
  //   },
  //   [assessments, assessmentsLength],
  // );

  const totalInvestigationsActiveAssessment = assessments?.length || 0;
  const hasMorePagesActiveAssessment = totalInvestigationsActiveAssessment >= 3;

   const handlePageChangeActiveAsses = async (newPage: number, type: DASHBOARD_TYPES) => {
      try {
        const { data } = await client.query({
          query: gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByTeacherLimited,
          variables: {
            filter: { page: newPage, pageSize, type },
          },
          fetchPolicy: 'network-only',
        });
  
        const nextPageData = data?.getInvestigationsByTeacherLimited || [];
  
        if (nextPageData.length === 0) {
          message.info(`No more ${type} available, returning to the previous page.`);
          return;
        }
  
        setPageActiveAssess(newPage);
      } catch (error) {
        message.error('Failed to load data. Please try again.');
      }
    };


  return (
    <>
      <DashboardRow
        title={`Active Assessments`}
        items={activeAssessments}
        // extraItems={extraActiveAssessments}
        // onViewMore={onViewMoreActiveAssessments}
        //showViewMore={assessmentsLength > 3}
      />
     <Pagination
                current={pageActiveAssess}
                pageSize={pageSize}
                total={hasMorePagesActiveAssessment ? (pageActiveAssess + 1) * pageSize : pageActiveAssess * pageSize}
                onChange={(newPage) => handlePageChangeActiveAsses(newPage, DASHBOARD_TYPES.ACTIVE_ASSESSMENTS)}
                showSizeChanger={false}
                hideOnSinglePage={totalInvestigationsActiveAssessment <= pageSize}
                style={{ textAlign: 'center', marginTop: 16 }}
                disabled={loading}
              />
    </>

  );
};

export default ActiveAssessmentsRow;
